import { API_BASE_URL, ACCESS_TOKEN } from "../constants";
import { jwtDecode } from "jwt-decode";

// ✅ Generic request handler
const request = async (options) => {
  const headers = new Headers({
    "Content-Type": "application/json",
  });

  const token = localStorage.getItem(ACCESS_TOKEN);
  if (token) {
    headers.append("Authorization", `Bearer ${token}`);
  }

  const defaults = { headers };
  options = { ...defaults, ...options };

  try {
    const response = await fetch(options.url, options);

    const text = await response.text(); // Get raw response body
    let json = {};
    try {
      json = text ? JSON.parse(text) : {};
    } catch (e) {
      console.error("Failed to parse response JSON:", e);
    }

    if (!response.ok) {
      return Promise.reject(json || response.statusText);
    }

    return json;
  } catch (error) {
    return Promise.reject(error);
  }
};

// ✅ Get current logged-in user (GET /login or /me)
export const getCurrentUser = () => {
  const token = localStorage.getItem(ACCESS_TOKEN);
  if (!token) {
    return Promise.reject("No access token set.");
  }

  return request({
    url: `${API_BASE_URL}/login`, // Change to /me if your API uses that
    method: "GET",
  });
};

export const logoutUser = async () => {
  const token = localStorage.getItem(ACCESS_TOKEN);
  const hasToken = !!token;

  try {
    if (hasToken) {
      const response = await fetch(`${API_BASE_URL}/logout`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Authorization": `Bearer ${token}`
        }
      });

      // Even if 401, we can still clean up locally
      if (!response.ok && response.status !== 401) {
        console.error(`Logout failed: ${response.statusText}`);
      }
    }
  } catch (error) {
    console.warn("Logout skipped or failed due to expired/invalid token:", error.message);
  } finally {
    // ✅ Always clean local data and redirect
    localStorage.clear();
    sessionStorage.clear();
    window.location.href = "/login";
  }
};

// ✅ Decode JWT to extract role, name, email, expiry
export const decodeJwt = () => {
  const token = localStorage.getItem(ACCESS_TOKEN);
  if (!token) throw new Error("No token to decode");

  const decodedToken = jwtDecode(token);
  const authorities = decodedToken.authorities || [];
  const expiration = decodedToken.exp * 1000; // ms
  const userEmail = decodedToken.sub;
  const userName = decodedToken.fullName;

  return { authorities, expiration, userEmail, userName };
};

export const isTokenExpired = () => {
  const token = localStorage.getItem(ACCESS_TOKEN);
  if (!token) return true;

  try {
    const { expiration } = decodeJwt();
    return Date.now() > expiration;
  } catch (err) {
    return true; // On error, treat as expired
  }
};
